import { useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Check } from "lucide-react";
import { api } from "../lib/api";
import DatePickerIT from "./ui/date-picker-it";

const LEVELS = [
  { v: "annual", label: "Anno" },
  { v: "monthly", label: "Mese" },
  { v: "weekly", label: "Settimana" },
];
const PRIORITIES = [
  { v: "low", label: "Bassa" },
  { v: "medium", label: "Media" },
  { v: "high", label: "Alta" },
  { v: "critical", label: "Critica" },
];

/**
 * Reusable modal for creating an Azione (mete row).
 *
 * Props:
 *  - defaultLevel: "annual" | "monthly" | "weekly"
 *  - onClose, onCreated
 *  - tree: full mete tree { annual, monthly, weekly } (for parent selector)
 *  - presetMetaId: preselects a personal_goal (Meta finale) to link this azione to.
 *                  When set, hides the parent selector and marks the azione as
 *                  "collegata alla meta" via meta_id.
 */
export default function CreateAzioneModal({
  defaultLevel = "weekly",
  onClose,
  onCreated,
  tree = { annual: [], monthly: [], weekly: [] },
  presetMetaId = null,
}) {
  const [form, setForm] = useState({
    title: "", description: "", level: defaultLevel,
    parent_meta_id: "", priority: "medium", deadline_date: "",
  });
  const [busy, setBusy] = useState(false);

  const parentOptions = useMemo(() => {
    if (form.level === "weekly") return [...tree.monthly, ...tree.annual].filter((m) => m.status === "active");
    if (form.level === "monthly") return tree.annual.filter((m) => m.status === "active");
    return [];
  }, [form.level, tree]);

  const submit = async () => {
    if (form.title.trim().length < 2) { toast.error("Il titolo deve avere almeno 2 caratteri"); return; }
    setBusy(true);
    try {
      const payload = {
        title: form.title, level: form.level,
        description: form.description || null,
        parent_meta_id: form.parent_meta_id || null,
        priority: form.priority,
        deadline_date: form.deadline_date || null,
      };
      if (presetMetaId) payload.meta_id = presetMetaId;
      await api.createMeta(payload);
      toast.success("Azione creata");
      try { onCreated?.(); }
      catch (e2) { console.warn("onCreated callback failed:", e2); }
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione azione");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose} data-testid="azione-create-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-semibold">Nuova azione{presetMetaId ? " collegata" : ""}</h3>
          <button onClick={onClose} data-testid="azione-modal-close" aria-label="Chiudi"><X size={20}/></button>
        </div>
        <label className="block text-sm">
          <div className="opacity-70 mb-1">Livello</div>
          <div className="inline-flex rounded-full border overflow-hidden" data-testid="azione-level-tabs">
            {LEVELS.map((l) => (
              <button key={l.v} type="button"
                onClick={() => setForm({ ...form, level: l.v, parent_meta_id: "" })}
                className={`px-3 py-1.5 text-xs ${form.level === l.v ? "bg-white shadow-inner font-semibold" : "opacity-70"}`}
                data-testid={`azione-level-${l.v}`}>
                {l.label}
              </button>
            ))}
          </div>
        </label>
        <label className="block text-sm">
          <div className="opacity-70 mb-1">Titolo *</div>
          <input className="pxos-input" value={form.title} maxLength={300}
                 onChange={(e) => setForm({ ...form, title: e.target.value })}
                 data-testid="azione-title-input"/>
        </label>
        <label className="block text-sm">
          <div className="opacity-70 mb-1">Descrizione</div>
          <textarea className="pxos-input" rows={3} value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    data-testid="azione-description-input"/>
        </label>
        {!presetMetaId && parentOptions.length > 0 && (
          <label className="block text-sm">
            <div className="opacity-70 mb-1">Azione genitore (opzionale)</div>
            <select className="pxos-input" value={form.parent_meta_id}
                    onChange={(e) => setForm({ ...form, parent_meta_id: e.target.value })}
                    data-testid="azione-parent-select">
              <option value="">— nessuna —</option>
              {parentOptions.map((p) => (
                <option key={p.id} value={p.id}>{p.title} ({p.level === "annual" ? "annuale" : "mensile"})</option>
              ))}
            </select>
          </label>
        )}
        {presetMetaId && (
          <div className="text-xs opacity-70 bg-black/5 rounded-lg p-2" data-testid="azione-linked-meta-hint">
            Questa azione verrà collegata automaticamente alla meta corrente.
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <div className="opacity-70 mb-1">Priorità</div>
            <select className="pxos-input" value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    data-testid="azione-priority-select">
              {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
            </select>
          </label>
          <label className="block text-sm">
            <div className="opacity-70 mb-1">Scadenza</div>
            <DatePickerIT value={form.deadline_date}
                          onChange={(v) => setForm({ ...form, deadline_date: v })}
                          testId="azione-deadline-input" placeholder="gg/mm/aaaa"/>
          </label>
        </div>
        <div className="flex gap-2 pt-2 justify-end">
          <button onClick={onClose} className="text-sm underline opacity-70" data-testid="azione-create-cancel">Annulla</button>
          <button disabled={busy} onClick={submit} className="pxos-btn-primary text-sm" data-testid="azione-create-submit">
            <Check size={14}/> Crea azione
          </button>
        </div>
      </div>
    </div>
  );
}
